import { useEffect } from "react";
import styled from "@emotion/styled";
import debounce from "lodash/debounce";

import { MAPBOX_TOKEN, MAPBOX_STYLE } from "config";
import { useMapState } from "contexts/MapStateContext";
import { useSimulations } from "contexts/SimulationsContext";
import { useRuns } from "contexts/RunsContext";
import { addMapSourcesAndLayers } from "../mapSetup";
import usePage from "hooks/usePage";
import usePrevious from "hooks/usePrevious";
import { MAP_CONTAINER_STYLES } from "constants/mapStyles";

const MapWrapper = styled.div`
  #map {
    position: ${MAP_CONTAINER_STYLES.position};
    top: ${MAP_CONTAINER_STYLES.top};
    bottom: ${MAP_CONTAINER_STYLES.bottom};
    left: ${MAP_CONTAINER_STYLES.left};
    right: ${MAP_CONTAINER_STYLES.right};
    width: ${MAP_CONTAINER_STYLES.width};
  }
  .mapboxgl-ctrl-geocoder {
    float: none !important;
    border-radius: 20px !important;
    margin-left: 55px !important;
  }
  .mapboxgl-ctrl-geocoder--input {
    border-radius: 20px !important;
    background-color: #fff !important;
  }

  .mapboxgl-ctrl-top-left {
    top: -2px;
    left: 4px;
  }
  .mapboxgl-ctrl-top-right {
    top: -2px;
    right: 4px;
    left: 0;
  }
  .mapboxgl-ctrl-bottom-right {
    left: 10px;
    right: auto;
    bottom: 0;
    display: flex;
  }
  .mapboxgl-ctrl-scale {
    position: relative;
  }
  .mapboxgl-ctrl-attrib {
    margin-bottom: 12px;
  }
  .mapboxgl-ctrl-bottom-left {
    left: 0;
    right: auto;
    bottom: 20px;
  }
  .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl,
  .mapboxgl-ctrl-top-right .mapboxgl-ctrl-geocoder {
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 0;
    box-shadow: ${({ theme }) => theme.shadows[1]}; // MUI Card shadow
  }

  .mapboxgl-ctrl-logo {
    display: none;
  }

  // For some reason there are duplicate copyright/attribution statements, so hide the dupes:
  .mapboxgl-ctrl-attrib-inner [title="Mapbox"] ~ [title="Mapbox"],
  .mapboxgl-ctrl-attrib-inner [title="OpenStreetMap"] ~ [title="OpenStreetMap"],
  .mapboxgl-ctrl-attrib-inner .mapbox-improve-map ~ .mapbox-improve-map {
    display: none;
  }

  .mapboxgl-popup {
    max-width: 400px;
    font:
      12px/20px "Helvetica Neue",
      Arial,
      Helvetica,
      sans-serif;
  }
`;

function flattenGeometryCollection(feature) {
  // Some KML placemarks (e.g. degenerate, zero-area polygons) parse to a null
  // geometry. Skip them so downstream geometry-type checks don't throw.
  if (!feature.geometry) {
    return [];
  }

  if (feature.geometry.type !== "GeometryCollection") {
    return [feature];
  }

  // Group geometries by type
  const polygons = [];
  const multiPolygons = [];
  const lineStrings = [];
  const multiLineStrings = [];
  const others = [];

  feature.geometry.geometries.forEach((geometry) => {
    switch (geometry.type) {
      case "Polygon":
        polygons.push(geometry.coordinates);
        break;
      case "MultiPolygon":
        multiPolygons.push(...geometry.coordinates);
        break;
      case "LineString":
        lineStrings.push(geometry.coordinates);
        break;
      case "MultiLineString":
        multiLineStrings.push(...geometry.coordinates);
        break;
      default:
        // Handle other geometry types as separate features
        others.push({
          ...feature,
          id: feature.id
            ? `${feature.id}_${geometry.type}`
            : `geom_${geometry.type}`,
          geometry: geometry,
        });
    }
  });

  const result = [];

  // Create MultiPolygon if we have polygons
  if (polygons.length > 0 || multiPolygons.length > 0) {
    const allPolygonCoords = [...polygons, ...multiPolygons];
    result.push({
      ...feature,
      id: feature.id ? `${feature.id}_poly` : `geom_poly`,
      geometry: {
        type: "MultiPolygon",
        coordinates: allPolygonCoords,
      },
    });
  }

  // Create MultiLineString if we have line strings
  if (lineStrings.length > 0 || multiLineStrings.length > 0) {
    const allLineCoords = [...lineStrings, ...multiLineStrings];
    result.push({
      ...feature,
      id: feature.id ? `${feature.id}_line` : `geom_line`,
      geometry: {
        type: "MultiLineString",
        coordinates: allLineCoords,
      },
    });
  }

  // Add any other geometry types as separate features
  result.push(...others);

  return result;
}

function splitShapes(features) {
  // First, flatten any GeometryCollection features
  const flattenedFeatures = [];
  features.forEach((feature) => {
    flattenedFeatures.push(...flattenGeometryCollection(feature));
  });

  const lines = flattenedFeatures.filter(
    (f) =>
      f.geometry.type === "LineString" || f.geometry.type === "MultiLineString"
  );
  const polys = flattenedFeatures.filter((f) =>
    ["Polygon", "MultiPolygon"].includes(f.geometry.type)
  );
  return { lines, polys };
}

function addFeatures(featureType, features) {
  let featureList;
  if (featureType) {
    featureList = featureType.features;
    features.forEach((f) => {
      if (!featureList.some((feature) => feature.id === f.id)) {
        featureList.push(f);
      }
    });
  } else {
    featureList = features;
  }
  return { type: "FeatureCollection", features: featureList };
}

function updateFeatures(featureType, features) {
  let featureList;
  if (featureType) {
    featureList = featureType.features;
    features.forEach((f) => {
      const index = featureList.findIndex((feature) => feature.id === f.id);
      if (index !== -1) {
        featureList[index] = f;
      }
    });
  } else {
    featureList = features;
  }
  return { type: "FeatureCollection", features: featureList };
}

function deleteFeatures(featureType, features) {
  let featureList;
  if (featureType) {
    featureList = featureType.features;
    features.forEach((f) => {
      const index = featureList.findIndex((feature) => feature.id === f.id);
      if (index !== -1) {
        featureList.splice(index, 1);
      }
    });
  } else {
    featureList = [];
  }
  return { type: "FeatureCollection", features: featureList };
}

export default function Map({ shareable = false, variant = "basic" }) {
  const {
    map,
    setMap,
    mapLoaded,
    setMapLoaded,
    mapContainer,
    mapboxGeocoderControl,
  } = useMapState();

  const {
    setIgnition,
    ignition,
    setOverrides,
    setShowOverrides,
    overrides,
    setClearIgnitionDrawing,
    uploadedOverrideGeoJson,
  } = useSimulations();

  const prevUploadedOverrideGeoJson = usePrevious(uploadedOverrideGeoJson);

  const { isCreatingSimulation } = useRuns();
  const { runId, setRunId } = usePage();

  useEffect(() => {
    if (shareable && mapLoaded && map) {
      setRunId(runId);
    }
  }, [shareable, map, runId, setRunId, mapLoaded]);

  useEffect(() => {
    // Do not initialize if the map is already loaded or the container isn't ready
    if (mapLoaded || !mapContainer.current) {
      return;
    }

    // Return if there's already a map in the container (prevents multiple maps)
    if (mapContainer.current._mapboxgl) {
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;

    let initialCenter = [-122, 37];
    let initialZoom = 7;

    if (window.location.hash) {
      const hashParts = window.location.hash.slice(1).split("/");
      if (hashParts.length >= 3) {
        const zoom = parseFloat(hashParts[0]);
        const lat = parseFloat(hashParts[1]);
        const lng = parseFloat(hashParts[2]);
        if (!isNaN(zoom) && !isNaN(lat) && !isNaN(lng)) {
          initialCenter = [lng, lat];
          initialZoom = zoom;
        }
      }
    }

    const newMap = new mapboxgl.Map({
      hash: true,
      style: MAPBOX_STYLE,
      container: mapContainer.current,
      center: initialCenter,
      zoom: initialZoom,
      doubleClickZoom: false,
    });

    // Store a reference to mark this container as having a map
    mapContainer.current._mapboxgl = newMap;

    const debouncedResize = debounce(() => {
      newMap.resize();
    }, 750);
    window.addEventListener("resize", debouncedResize);

    if (!shareable) {
      newMap.addControl(mapboxGeocoderControl, "top-right");
    }

    const nav = new mapboxgl.NavigationControl({
      showCompass: true,
    });
    if (shareable) {
      document.querySelector(".mapboxgl-ctrl-top-left").style.left = "0px";
    }
    newMap.addControl(nav, "top-left");

    const scale = new mapboxgl.ScaleControl({
      maxWidth: 100,
      unit: "imperial",
    });
    newMap.addControl(scale, "bottom-left");

    newMap.on("load", () => {
      setMap(newMap);
      addMapSourcesAndLayers(newMap);
      setMapLoaded(true);
    });

    return () => {
      window.removeEventListener("resize", debouncedResize);
    };
  }, [
    mapLoaded,
    mapContainer,
    setMapLoaded,
    setMap,
    shareable,
    mapboxGeocoderControl,
  ]);

  useEffect(() => {
    return () => {
      setMapLoaded(false);
    };
  }, [setMapLoaded]);

  useEffect(() => {
    if (!map) {
      return;
    }

    let draw;

    if (isCreatingSimulation) {
      draw = new MapboxDraw({
        controls: {
          polygon: true,
          point: false,
          line_string: true,
          trash: true,
          combine_features: false,
          uncombine_features: false,
        },
      });
      map.addControl(draw, "top-left");

      const clearDrawing = () => draw.deleteAll();
      setClearIgnitionDrawing(() => clearDrawing);
      if (ignition) {
        draw.add(ignition);
      }
      if (overrides) {
        // setShowOverrides(true);
        draw.add(overrides);
      }
    } else {
      const existingDrawControl = map._controls.find(
        (control) => control instanceof MapboxDraw
      );
      if (existingDrawControl) {
        map.removeControl(existingDrawControl);
      }
    }

    return () => {
      if (draw) {
        map.removeControl(draw);
      }
    };
  }, [map, isCreatingSimulation, setClearIgnitionDrawing, ignition, overrides]);

  // if use upload override geojson, add the features to the map
  useEffect(() => {
    if (!uploadedOverrideGeoJson || !map) {
      return;
    }

    if (prevUploadedOverrideGeoJson === uploadedOverrideGeoJson) {
      return;
    }

    const { polys } = splitShapes(uploadedOverrideGeoJson.features);

    console.log("Map.jsx: uploadedOverrideGeoJson", {
      uploadedOverrideGeoJson,
      polys,
    });

    if (polys.length) {
      setShowOverrides(true);
      setOverrides(addFeatures(overrides, polys));
    }
  }, [
    uploadedOverrideGeoJson,
    overrides,
    setShowOverrides,
    setOverrides,
    map,
    prevUploadedOverrideGeoJson,
  ]);

  // if user delete the uploaded override geojson, delete the features from the map
  useEffect(() => {
    if (!map) {
      return;
    }
    if (prevUploadedOverrideGeoJson && uploadedOverrideGeoJson === null) {
      const { polys } = splitShapes(prevUploadedOverrideGeoJson.features);
      if (polys.length) {
        setOverrides(deleteFeatures(overrides, polys));
      }
    }
  }, [
    uploadedOverrideGeoJson,
    overrides,
    setOverrides,
    map,
    prevUploadedOverrideGeoJson,
  ]);

  useEffect(() => {
    if (!map) {
      return;
    }
    map.on("draw.create", function (e) {
      const { lines, polys } = splitShapes(e.features);
      if (lines.length) {
        setIgnition(addFeatures(ignition, lines));
      }

      if (polys.length) {
        setShowOverrides(true);
        setOverrides(addFeatures(overrides, polys));
      }
    });

    map.on("draw.update", function (e) {
      const { lines, polys } = splitShapes(e.features);

      if (lines.length) {
        setIgnition(updateFeatures(ignition, lines));
      }

      if (polys.length) {
        setOverrides(updateFeatures(overrides, polys));
      }
    });

    map.on("draw.delete", function (e) {
      const { lines, polys } = splitShapes(e.features);

      if (lines.length) {
        setIgnition(deleteFeatures(ignition, lines));
      }

      if (polys.length) {
        setOverrides(deleteFeatures(overrides, polys));
      }
    });
  }, [map, ignition, overrides, setIgnition, setShowOverrides, setOverrides]);

  return (
    <MapWrapper>
      <div ref={mapContainer} id="map" />
    </MapWrapper>
  );
}
